import { Reference } from '@approvalmax/types';
import { constants } from 'modules/common';
import { backend } from 'modules/data';
import { useCallback } from 'react';

import { useXeroBillBatchPaymentRequestContext } from '../../XeroBillBatchPaymentRequestContext';
import { MainInformationProps } from './MainInformation.types';

const { xeroConstants } = constants;

export const useMainInformationData = (countryCode: MainInformationProps['countryCode']) => {
    const { billBatchPayment, setBillBatchPayment, showErrors, editMode } = useXeroBillBatchPaymentRequestContext();

    const onPaymentDateChange = useCallback(
        (value: string | null) => {
            setBillBatchPayment({ ...billBatchPayment, paymentDate: value }, 'paymentDate');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onBankAccountChange = useCallback(
        (value: Reference | null) => {
            setBillBatchPayment({ ...billBatchPayment, bankAccount: value }, 'bankAccount');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onNarrativeChange = useCallback(
        (value: string) => {
            setBillBatchPayment({ ...billBatchPayment, narrative: value }, 'narrative');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onDetailsChange = useCallback(
        (value: string) => {
            setBillBatchPayment({ ...billBatchPayment, details: value }, 'details');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onParticularsChange = useCallback(
        (value: string) => {
            setBillBatchPayment({ ...billBatchPayment, particulars: value }, 'particulars');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onCodeChange = useCallback(
        (value: string) => {
            setBillBatchPayment({ ...billBatchPayment, code: value }, 'code');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const onReferenceChange = useCallback(
        (value: string) => {
            setBillBatchPayment({ ...billBatchPayment, reference: value }, 'reference');
        },
        [billBatchPayment, setBillBatchPayment]
    );

    const isInvalidNarrative = !!(
        billBatchPayment.narrative &&
        (!billBatchPayment.narrative?.trim() ||
            billBatchPayment.narrative.length > xeroConstants.BATCH_PAYMENT_NARRATIVE_MAX_LENGTH)
    );

    const referenceFieldMaxLength =
        countryCode === backend.OrganisationVersion.AU
            ? xeroConstants.BATCH_PAYMENT_REFERENCE_AU_MAX_LENGTH
            : xeroConstants.BATCH_PAYMENT_REFERENCE_DEFAULT_MAX_LENGTH;

    const isInvalidReference = !!(
        billBatchPayment.reference &&
        (!billBatchPayment.reference?.trim() || billBatchPayment.reference.length > referenceFieldMaxLength)
    );

    const isInvalidDetails = !!(
        billBatchPayment.details &&
        (!billBatchPayment.details?.trim() ||
            billBatchPayment.details.length > xeroConstants.BATCH_PAYMENT_DETAILS_MAX_LENGTH)
    );

    const isInvalidParticulars = !!(
        billBatchPayment.particulars &&
        (!billBatchPayment.particulars?.trim() ||
            billBatchPayment.particulars.length > xeroConstants.BATCH_PAYMENT_PARTICULARS_MAX_LENGTH)
    );

    const isInvalidCode = !!(
        billBatchPayment.code &&
        (!billBatchPayment.code?.trim() || billBatchPayment.code.length > xeroConstants.BATCH_PAYMENT_CODE_MAX_LENGTH)
    );

    const isUkCompany = countryCode === backend.OrganisationVersion.UK;

    return {
        billBatchPayment,
        showErrors,
        onPaymentDateChange,
        onBankAccountChange,
        onNarrativeChange,
        onDetailsChange,
        onParticularsChange,
        onCodeChange,
        onReferenceChange,
        referenceFieldMaxLength,
        isInvalidNarrative,
        isInvalidReference,
        isInvalidDetails,
        isInvalidParticulars,
        isInvalidCode,
        isUkCompany,
        editMode,
    };
};
